// Assets: the files grid (spec/dashboard.md section 9.7). The grid over the branch's assets from the catalog with the
// draft overlay (record edits are staged `asset` drafts saved with PATCH + If-Match on Save All); filters Kind, Group,
// search (name, description, legacy id) and Show deleted (the soft-deleted records, greyed, with their deletion time);
// Upload… (the panel); the row menu Open / Where used / Delete | Restore — commands with their confirmation, never
// staged; a Delete answered with `409` lists the referencing documents and deletes nothing.
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Asset } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useContent } from '../../content/ContentContext';
import { useGame } from '../../shell/GameContext';
import { EmptyState } from '../../ui/Banner';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { DataGrid, type Column } from '../../ui/DataGrid';
import { AlertCircle, Images, MoreVertical, Search, Trash2, Upload } from '../../ui/icons';
import { Select, Switch } from '../../ui/inputs';
import { Modal, useUrlParam } from '../../ui/Modal';
import { Menu } from '../../ui/Popover';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { useQuery } from '../../workspace/cache';
import { useWorkspace, useWorkspaceVersion } from '../../workspace/react';
import { entityKey, type Draft } from '../../workspace/Workspace';
import { AssetDrawer } from './AssetDrawer';
import { AssetPreview, errorText, formatBytes, GroupSelect, InlineText, metaSummary, ReferenceList, refsFromConflict, stageAsset, useAssetReferenceLoader, type AssetRef } from './common';
import { UploadPanel } from './UploadPanel';

interface Row {
  /** the server record (the draft's base) */
  server: Asset;
  /** what the user sees: the draft over the server record */
  asset: Asset;
  draft: Draft | undefined;
  deleted: boolean;
}

const NO_GROUP = '__hue:no-group__';

export function FilesTab() {
  const { org, game, can, offsets } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const catalog = useContent();
  const workspace = useWorkspace();
  const version = useWorkspaceVersion(workspace);
  const confirm = useConfirm();
  const toasts = useToasts();
  const [zone] = useTimeZone();
  const loadRefs = useAssetReferenceLoader();
  const canWrite = can('content_write') && !workspace.isProtected;
  const path = { org, game: game.slug, branch };

  const [kind, setKind] = useState('');
  const [group, setGroup] = useState('');
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [whereUsed, setWhereUsed] = useState<{ asset: Asset; refs: AssetRef[] | null; error?: string } | null>(null);
  const [blocked, setBlocked] = useState<{ asset: Asset; refs: AssetRef[]; message: string } | null>(null);
  const assetParam = useUrlParam('asset');

  const deletedQ = useQuery(showDeleted ? `branch:${game.id}:${branch}:assets:deleted` : null, () => client.all<Asset>((cursor) => client.data(ops.listAssets, { path, query: { deleted: 'true', limit: 200, cursor } })), { head: workspace.head });

  const rows = useMemo<Row[]>(() => {
    const live = catalog.assetList.map<Row>((server) => {
      const draft = workspace.getDraft(entityKey('asset', server.id));
      return { server, asset: (draft?.current as unknown as Asset | undefined) ?? server, draft, deleted: false };
    });
    const gone = showDeleted ? (deletedQ.data ?? []).map<Row>((server) => ({ server, asset: server, draft: undefined, deleted: true })) : [];
    return [...live, ...gone].sort((a, b) => a.asset.name.localeCompare(b.asset.name) || a.asset.id.localeCompare(b.asset.id));
  }, [catalog.assetList, deletedQ.data, showDeleted, workspace, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => Array.from(new Set(rows.filter((r) => !r.deleted).map((r) => r.asset.group).filter((g): g is string => !!g))).sort(), [rows]);
  const kinds = useMemo(() => Array.from(new Set(rows.map((r) => r.asset.kind))).sort(), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (kind && r.asset.kind !== kind) return false;
      if (group === NO_GROUP ? !!r.asset.group : group && r.asset.group !== group) return false;
      if (q && ![r.asset.name, r.asset.description ?? '', r.asset.unnyId ?? ''].some((t) => t.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, kind, group, search]);

  const openAsset = (id: string) => assetParam.open(id);

  const remove = async (asset: Asset) => {
    if (workspace.getDraft(entityKey('asset', asset.id))) {
      toasts.error(`Save or discard the record edits of ${asset.name} first: the deletion is a commit that moves its version.`);
      return;
    }
    const ok = await confirm({
      title: `Delete ${asset.name}?`,
      body: 'The record is soft-deleted (Restore brings it back with its id); the bytes stay in the store. A document that still references the asset blocks the deletion and is listed. This is a commit of its own, not a staged change.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await client.call(ops.deleteAsset, { path: { ...path, asset: asset.id }, ifMatch: Number(asset.version) });
      toasts.success(`${asset.name} deleted.`);
      if (assetParam.value === asset.id) assetParam.close();
      catalog.refresh();
      deletedQ.refetch();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        let refs = refsFromConflict(e);
        // the references endpoint carries each document's template (the links need it); the 409's list stays the truth
        try {
          const full = await loadRefs(asset.id);
          if (full.length > 0) refs = refs.length === 0 ? full : refs.map((r) => ({ ...r, template: full.find((f) => f.documentId === r.documentId)?.template ?? r.template }));
        } catch {
          // the 409's own list is enough
        }
        setBlocked({ asset, refs, message: e.message });
      } else toasts.error(errorText(e), { requestId: e instanceof ApiError ? e.requestId : undefined });
    }
  };

  const restore = async (asset: Asset) => {
    try {
      await client.call(ops.restoreAsset, { path: { ...path, asset: asset.id } });
      toasts.success(`${asset.name} restored.`);
      catalog.refresh();
      deletedQ.refetch();
    } catch (e) {
      toasts.error(errorText(e), { requestId: e instanceof ApiError ? e.requestId : undefined });
    }
  };

  const showWhereUsed = (asset: Asset) => {
    setWhereUsed({ asset, refs: null });
    loadRefs(asset.id).then(
      (refs) => setWhereUsed((w) => (w && w.asset.id === asset.id ? { asset, refs } : w)),
      (e: unknown) => setWhereUsed((w) => (w && w.asset.id === asset.id ? { asset, refs: [], error: errorText(e) } : w)),
    );
  };

  const editable = (r: Row) => canWrite && !r.deleted;
  const columns: Column<Row>[] = [
    { key: 'preview', header: 'Preview', width: 56, fixed: true, render: (r) => <AssetPreview asset={r.asset} /> },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      sortValue: (r) => r.asset.name,
      cellText: (r) => r.asset.name,
      render: (r) => (
        <span className="row asset-name-cell">
          <button type="button" className="asset-name" onClick={() => openAsset(r.asset.id)} aria-label={`Open ${r.asset.name}`}>
            {r.asset.name}
          </button>
          {r.draft && <Badge tone="warning">MOD</Badge>}
          {r.deleted && (
            <Badge tone="danger" title="Soft-deleted: Restore brings it back">
              DEL
            </Badge>
          )}
        </span>
      ),
      editor: (r, done) => (editable(r) ? <InlineText label={`Name of ${r.server.name}`} value={r.asset.name} onCancel={done.cancel} onCommit={(v) => { if (v.trim()) stageAsset(workspace, r.server, 'name', v.trim()); done.commit(v); }} /> : r.asset.name),
    },
    { key: 'kind', header: 'Kind', sortable: true, sortValue: (r) => r.asset.kind, cellText: (r) => r.asset.kind, render: (r) => <Badge>{r.asset.kind}</Badge> },
    { key: 'size', header: 'Size', align: 'right', sortable: true, sortValue: (r) => (r.asset.bytes === null ? null : Number(r.asset.bytes)), cellText: (r) => formatBytes(r.asset.bytes), render: (r) => formatBytes(r.asset.bytes) },
    {
      key: 'group',
      header: 'Group',
      sortable: true,
      sortValue: (r) => r.asset.group ?? '',
      cellText: (r) => r.asset.group ?? '',
      render: (r) => r.asset.group ?? <span className="muted">—</span>,
      editor: (r, done) =>
        editable(r) ? (
          <GroupSelect
            size="s"
            autoFocus
            label={`Group of ${r.server.name}`}
            value={r.asset.group}
            groups={groups}
            onCancel={done.cancel}
            onChange={(g) => {
              stageAsset(workspace, r.server, 'group', g);
              done.commit(g);
            }}
          />
        ) : (
          (r.asset.group ?? '—')
        ),
    },
    {
      key: 'description',
      header: 'Description',
      cellText: (r) => r.asset.description ?? '',
      render: (r) => r.asset.description ?? <span className="muted">—</span>,
      editor: (r, done) =>
        editable(r) ? (
          <InlineText
            label={`Description of ${r.server.name}`}
            value={r.asset.description ?? ''}
            onCancel={done.cancel}
            onCommit={(v) => {
              stageAsset(workspace, r.server, 'description', v === '' ? null : v);
              done.commit(v);
            }}
          />
        ) : (
          (r.asset.description ?? '—')
        ),
    },
    { key: 'meta', header: 'Meta', cellText: (r) => metaSummary(r.asset.meta), render: (r) => <span title={r.asset.meta === null || r.asset.meta === undefined ? undefined : JSON.stringify(r.asset.meta)}>{metaSummary(r.asset.meta)}</span> },
    { key: 'flags', header: 'Flags', align: 'right', sortable: true, sortValue: (r) => Number(r.asset.flags ?? 0), render: (r) => (Number(r.asset.flags ?? 0) === 0 ? <span className="muted">—</span> : String(r.asset.flags)) },
    {
      key: 'id',
      header: 'Id / legacy id',
      cellText: (r) => r.asset.unnyId ?? r.asset.id,
      render: (r) => (
        <span className="stack asset-ids">
          <code title={r.asset.id}>{r.asset.id.slice(0, 8)}</code>
          {r.asset.unnyId && <code className="muted">{r.asset.unnyId}</code>}
        </span>
      ),
    },
    ...(showDeleted
      ? [
          {
            key: 'deletedAt',
            header: 'Deleted',
            sortable: true,
            sortValue: (r: Row) => r.asset.deletedAt ?? '',
            render: (r: Row) => (r.deleted ? <span className="small">{formatInstantWithZone(r.asset.deletedAt, zone, { offsets })}</span> : <span className="muted">—</span>),
          } as Column<Row>,
        ]
      : []),
    {
      key: 'menu',
      header: '',
      width: 40,
      fixed: true,
      render: (r) => <RowMenu row={r} canWrite={canWrite} onOpen={() => openAsset(r.asset.id)} onWhereUsed={() => showWhereUsed(r.server)} onDelete={() => void remove(r.server)} onRestore={() => void restore(r.server)} />,
    },
  ];

  // the drawer: the catalog's record, a deleted one from the list, else a direct read (a deep link to a deleted asset)
  const drawerId = assetParam.value;
  const drawerLocal = drawerId ? (catalog.assets.get(drawerId) ?? (deletedQ.data ?? []).find((a) => a.id === drawerId)) : undefined;
  const drawerQ = useQuery(drawerId && !drawerLocal && !catalog.loading ? `branch:${game.id}:${branch}:asset:${drawerId}` : null, () => client.data(ops.getAsset, { path: { ...path, asset: drawerId! } }) as unknown as Promise<Asset>, { head: workspace.head });
  const drawerServer = drawerLocal ?? drawerQ.data;

  const empty =
    catalog.error && !catalog.loading ? (
      <EmptyState icon={<AlertCircle />} title="The assets could not be loaded" body={catalog.error} />
    ) : rows.length === 0 ? (
      showDeleted ? (
        <EmptyState icon={<Trash2 />} title="No assets, and none deleted" body="This branch has no asset records, live or soft-deleted." />
      ) : (
        <EmptyState
          icon={<Images />}
          title="No assets on this branch yet"
          body="Upload adds images, audio, fonts, JSON or any other file; documents reference them by id."
          action={canWrite ? <Button size="s" icon={<Upload aria-hidden="true" />} onClick={() => setUploadOpen(true)}>Upload files</Button> : undefined}
        />
      )
    ) : (
      <EmptyState icon={<Search />} title="No asset matches the filters" body="Change the kind, the group or the search to see more assets." />
    );

  return (
    <section className="assets-files">
      <div data-testid="assets-grid">
      <DataGrid
        id="assets"
        previews
        rows={filtered}
        rowKey={(r) => r.asset.id}
        loading={catalog.loading || (showDeleted && deletedQ.loading)}
        revision={version + catalog.version}
        caption={`${filtered.length} asset${filtered.length === 1 ? '' : 's'}${filtered.length !== rows.length ? ` of ${rows.length}` : ''}`}
        search={{ value: search, onSearch: setSearch, placeholder: 'Search name, description, legacy id…' }}
        pageResetKey={[kind, group, showDeleted].join('|')}
        toolbar={
          <div className="asset-toolbar">
            <Select size="s" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
              <option value="">All kinds</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </Select>
            <Select size="s" value={group} onChange={(e) => setGroup(e.target.value)} aria-label="Group">
              <option value="">All groups</option>
              <option value={NO_GROUP}>(no group)</option>
              {groups.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </Select>
            <Switch label="Show deleted" aria-label="Show deleted" checked={showDeleted} onChange={setShowDeleted} />
            <span className="spacer" />
            <Button variant="primary" aria-label="Upload assets" icon={<Upload aria-hidden="true" />} onClick={() => setUploadOpen(true)} missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected} title={workspace.isProtected ? 'Protected branch: changes land through merge requests.' : undefined}>
              Upload…
            </Button>
          </div>
        }
        onRowOpen={(r) => openAsset(r.asset.id)}
        rowClass={(r) => (r.deleted ? 'asset-deleted' : r.draft ? 'dirty' : '')}
        emptyState={empty}
        columns={columns}
      />
      </div>
      {deletedQ.error !== undefined && showDeleted && (
        <div className="asset-alert" role="alert">
          <AlertCircle className="asset-alert-icon" aria-hidden="true" />
          <span>The deleted records could not be listed: {errorText(deletedQ.error)}</span>
        </div>
      )}
      {uploadOpen && <UploadPanel open onClose={() => setUploadOpen(false)} groups={groups} defaultGroup={group && group !== NO_GROUP ? group : null} />}
      {drawerId && <AssetDrawer key={drawerId} server={drawerServer} loading={catalog.loading || drawerQ.loading} groups={groups} canWrite={canWrite} onClose={assetParam.close} onDelete={(a) => void remove(a)} onRestore={(a) => void restore(a)} />}
      {whereUsed && (
        <Modal title={`Where used: ${whereUsed.asset.name}`} open onClose={() => setWhereUsed(null)} size="m">
          <div data-testid="asset-where-used">
            {whereUsed.error ? (
              <div className="asset-alert" role="alert">
                <AlertCircle className="asset-alert-icon" aria-hidden="true" />
                <span>{whereUsed.error}</span>
              </div>
            ) : whereUsed.refs === null ? (
              <div className="skeleton-rows" role="status" aria-label="Loading">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="skeleton-row">
                    <span className="skeleton" />
                    <span className="skeleton" />
                  </div>
                ))}
              </div>
            ) : (
              <ReferenceList refs={whereUsed.refs} />
            )}
          </div>
        </Modal>
      )}
      {blocked && (
        <Modal title={`${blocked.asset.name} is still referenced`} open onClose={() => setBlocked(null)} size="m" footer={<Button onClick={() => setBlocked(null)}>Close</Button>}>
          <div data-testid="asset-references" className="asset-dialog-body">
            <p className="asset-blocked-lede">
              Nothing was deleted: {blocked.refs.length === 1 ? 'a document references' : `${blocked.refs.length} references in documents point to`} this asset. Remove the references first, then delete it.
            </p>
            <p className="muted small">{blocked.message}</p>
            <ReferenceList refs={blocked.refs} empty="The server named no references." />
          </div>
        </Modal>
      )}
    </section>
  );
}

function RowMenu({ row, canWrite, onOpen, onWhereUsed, onDelete, onRestore }: { row: Row; canWrite: boolean; onOpen: () => void; onWhereUsed: () => void; onDelete: () => void; onRestore: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        label={`Actions for ${row.asset.name}`}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVertical aria-hidden="true" />
      </IconButton>
      <Menu
        anchor={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        label="Asset actions"
        items={[
          { key: 'open', label: 'Open', onSelect: onOpen },
          { key: 'where', label: 'Where used', onSelect: onWhereUsed },
          { key: 's1', label: '', separator: true },
          row.deleted ? { key: 'restore', label: 'Restore', onSelect: onRestore, disabled: !canWrite } : { key: 'delete', label: 'Delete', onSelect: onDelete, danger: true, disabled: !canWrite },
        ]}
      />
    </>
  );
}
